import type { DataItemId } from '@pn/core/domain/data';
import type { IntelWorkspaceItem } from '@pn/core/domain/intel/intel-workspace-item';
import type { LayerStyle } from '@pn/core/domain/layer';
import type { Query } from '@pn/core/domain/query';
import {
  createSourceItemMapConfig,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { findOrThrow } from '@pn/core/utils/logic';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';

export type IntelWorkspaceItemsState = {
  areaFilterWorkspaceItemId: string | null;
  areaGeometryFilter: GeoJSON.Geometry | null;
  provinceFilter: string | null;
  intelWorkspaceItems: IntelWorkspaceItem[];
  fetching: IntelWorkspaceItem['id'][];
  selectedIntelWorkspaceItemId: IntelWorkspaceItem['id'] | undefined;
};

const initialState: IntelWorkspaceItemsState = {
  areaFilterWorkspaceItemId: null,
  areaGeometryFilter: null,
  provinceFilter: null,
  intelWorkspaceItems: [],
  fetching: [],
  selectedIntelWorkspaceItemId: undefined,
};

const createIntelQueriesSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].isFetching = true;
          state.intelWorkspaceItems[index]._needsProcessing = false;
          state.intelWorkspaceItems[index].failedToFetch = false;
        }
      },
      receive: (
        state,
        action: PayloadAction<{
          id: IntelWorkspaceItem['id'];
          items: IntelWorkspaceItem['tableDataItems'];
        }>
      ) => {
        const { payload } = action;
        const { id, items } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].tableDataItems = items;
          state.intelWorkspaceItems[index].isFetching = false;
          if (state.intelWorkspaceItems[index].mapPlottingEnabled) {
            state.intelWorkspaceItems[index]._needsVisualProcessing = true;
            state.intelWorkspaceItems[index].query.checkedIds = items.map(
              (item) => item._id
            );
          }
        }
      },
      error: (
        state,
        action: PayloadAction<{ id: IntelWorkspaceItem['id'] }>
      ) => {
        const { payload } = action;
        const { id } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].isFetching = false;
          state.intelWorkspaceItems[index].failedToFetch = true;
        }
      },
      retry: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        const { payload } = action;

        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === payload
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index]._needsProcessing = true;
        }
      },
      create: (state, action: PayloadAction<IntelWorkspaceItem>) => {
        state.intelWorkspaceItems.push(action.payload);
      },
      add: (state, action: PayloadAction<IntelWorkspaceItem[]>) => {
        state.intelWorkspaceItems = state.intelWorkspaceItems.concat(
          action.payload
        );
      },
      update: (state, action: PayloadAction<IntelWorkspaceItem>) => {
        const { payload: updatedQuery } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === updatedQuery.id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index] = updatedQuery;
          state.intelWorkspaceItems[index]._needsProcessing = true;
        }
      },
      select: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        state.selectedIntelWorkspaceItemId = action.payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query: IntelWorkspaceItem) => query.id === action.payload
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index]._avoidLoadingUntilVisible = false;
        }
      },
      unselect: (state) => {
        state.selectedIntelWorkspaceItemId = undefined;
      },
      updateFilter: (
        state,
        action: PayloadAction<{ id: string; filter: Query['filters'][0] }>
      ) => {
        const { payload } = action;
        const { id, filter } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          const filters = state.intelWorkspaceItems[index].query.filters;
          const filterIndex = filters.findIndex(
            (f) => f.field === filter.field
          );
          if (filterIndex !== -1) {
            filters[filterIndex] = filter;
          } else {
            filters.push(filter);
          }
          state.intelWorkspaceItems[index]._needsProcessing = true;
        }
      },
      updateFilters: (
        state,
        action: PayloadAction<{ id: string; filters: Query['filters'] }>
      ) => {
        const { payload } = action;
        const { id, filters } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.filters = filters;
          state.intelWorkspaceItems[index]._needsProcessing = true;
        }
      },
      updateSorts: (
        state,
        action: PayloadAction<{ id: string; sorts: Query['sorts'] }>
      ) => {
        const { payload } = action;
        const { id, sorts } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.sorts = sorts;
        }
      },
      removeFilterByField: (
        state,
        action: PayloadAction<{ id: string; field: string }>
      ) => {
        const { payload } = action;
        const { id, field } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.filters =
            state.intelWorkspaceItems[index].query.filters.filter(
              (filter) => filter.field !== field
            );
          state.intelWorkspaceItems[index]._needsProcessing = true;
        }
      },
      remove: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems.splice(index, 1);
        }
      },
      toggleHideFromWorkspace: (
        state,
        action: PayloadAction<IntelWorkspaceItem['id']>
      ) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        const updatedHideFromWorkspace =
          !state.intelWorkspaceItems[index].hideFromWorkspace;
        if (index !== -1) {
          state.intelWorkspaceItems[index].hideFromWorkspace =
            updatedHideFromWorkspace;
          state.intelWorkspaceItems[index].mapPlottingEnabled =
            !updatedHideFromWorkspace;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
        }
      },
      checkAll: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.checkedIds =
            state.intelWorkspaceItems[index].tableDataItems.map(
              (item) => item._id
            );
          state.intelWorkspaceItems[index].mapPlottingEnabled = true;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
        }
      },
      uncheckAll: (state, action: PayloadAction<IntelWorkspaceItem['id']>) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.checkedIds = [];
          state.intelWorkspaceItems[index].mapPlottingEnabled = false;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
        }
      },
      check: (
        state,
        action: PayloadAction<{
          id: IntelWorkspaceItem['id'];
          intelDataId: DataItemId;
        }>
      ) => {
        const { payload } = action;
        const { id, intelDataId } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].query.checkedIds.push(intelDataId);
          state.intelWorkspaceItems[index].mapPlottingEnabled = true;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
        }
      },
      uncheck: (
        state,
        action: PayloadAction<{
          id: IntelWorkspaceItem['id'];
          intelDataId: DataItemId;
        }>
      ) => {
        const { payload } = action;
        const { id, intelDataId } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (query: IntelWorkspaceItem) => query.id === id
        );
        if (index !== -1) {
          const newCheckedIds = state.intelWorkspaceItems[
            index
          ].query.checkedIds.filter((id) => id !== intelDataId);
          state.intelWorkspaceItems[index].query.checkedIds = newCheckedIds;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
          if (isEmpty(newCheckedIds)) {
            state.intelWorkspaceItems[index].mapPlottingEnabled = false;
          }
        }
      },
      clearWorkspace: (state) => {
        state.intelWorkspaceItems = state.intelWorkspaceItems.filter(
          (query) => !isEmpty(query.linkedReportId)
        );
      },
      markAsVisuallyProcessed: (
        state,
        action: PayloadAction<IntelWorkspaceItem['id']>
      ) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (query) => query.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index]._needsVisualProcessing = false;
        }
      },
      updateAreaFilter: (
        state,
        action: PayloadAction<{
          geometry: GeoJSON.Geometry | null;
          workspaceItemId: string | null;
        }>
      ) => {
        state.areaGeometryFilter = action.payload.geometry;
        state.areaFilterWorkspaceItemId = action.payload.workspaceItemId;
        state.provinceFilter = null;
        state.intelWorkspaceItems.forEach((query) => {
          query._needsProcessing = true;
        });
      },
      updateProvinceFilter: (state, action: PayloadAction<string | null>) => {
        state.areaGeometryFilter = null;
        state.areaFilterWorkspaceItemId = null;
        state.provinceFilter = action.payload;
        state.intelWorkspaceItems.forEach((query) => {
          query._needsProcessing = true;
        });
      },
      clearGlobalFilters: (state) => {
        state.areaGeometryFilter = null;
        state.areaFilterWorkspaceItemId = null;
        state.provinceFilter = null;
        state.intelWorkspaceItems.forEach((query) => {
          query._needsProcessing = true;
        });
      },

      updateName: (
        state,
        action: PayloadAction<{ id: string; name: string }>
      ) => {
        const { payload } = action;
        const { id, name } = payload;
        const index = state.intelWorkspaceItems.findIndex(
          (item) => item.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].name = name;
        }
      },
      removeFromMap: (
        state,
        action: PayloadAction<IntelWorkspaceItem['id']>
      ) => {
        const { payload: id } = action;
        const index = state.intelWorkspaceItems.findIndex(
          (item) => item.id === id
        );
        if (index !== -1) {
          state.intelWorkspaceItems[index].mapPlottingEnabled = false;
          state.intelWorkspaceItems[index]._needsVisualProcessing = true;
        }
      },
      removeByReportId: (state, action: PayloadAction<string>) => {
        state.intelWorkspaceItems = state.intelWorkspaceItems.filter(
          (item) => item.linkedReportId !== action.payload
        );
      },
      updateAllLayerStyles: (
        state,
        { payload }: PayloadAction<{ itemId: string; style: LayerStyle }>
      ) => {
        const item = findOrThrow(
          state.intelWorkspaceItems,
          (item) => item.id === payload.itemId
        ) as WorkspaceItem;
        item.map = createSourceItemMapConfig({
          mode: 'duplicate',
          sourceItem: item,
          newLayerStyle: payload.style,
        });
        item.isProcessed = false;
      },
    },
  });

export const intelWorkspaceItemsSlice = createIntelQueriesSlice('intelQueries');
